import { useState, useEffect, useCallback } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { toast } from 'sonner';
import api from '@/lib/api';
import {
  Sparkles,
  TrendingUp,
  TrendingDown,
  Minus,
  Trophy,
  AlertCircle,
  Target,
  Calendar,
  BookOpen,
  Briefcase,
  Lightbulb,
  ShieldCheck,
  Clock,
  RefreshCw,
  ChevronRight,
} from 'lucide-react';

const TIER_STYLES = {
  'Top 10%': { color: 'text-emerald-700 dark:text-emerald-300', bg: 'from-emerald-500 to-teal-500', emoji: '🏆' },
  'Top 25%': { color: 'text-blue-700 dark:text-blue-300', bg: 'from-blue-500 to-indigo-500', emoji: '🥈' },
  'Solid': { color: 'text-violet-700 dark:text-violet-300', bg: 'from-violet-500 to-fuchsia-500', emoji: '👍' },
  'Needs Attention': { color: 'text-amber-700 dark:text-amber-300', bg: 'from-amber-500 to-orange-500', emoji: '⚠️' },
  'Critical': { color: 'text-red-700 dark:text-red-300', bg: 'from-red-500 to-rose-500', emoji: '🚨' },
};

const TREND_ICON = {
  Improving: TrendingUp,
  Stable: Minus,
  Declining: TrendingDown,
};
const TREND_COLOR = {
  Improving: 'text-emerald-600',
  Stable: 'text-slate-500',
  Declining: 'text-red-600',
};

const DEFAULT_FILTERS = {
  date_from: null,
  date_to: null,
  min_duration_seconds: 0,
  call_type: null,
  classification: null,
  sample_size: 25,
};

/**
 * Cumulative Expert Learning Report dialog.
 * Props:
 *   userId         (required)          — id of the user being coached
 *   userName       (string, optional)  — for header
 *   open, onOpenChange                 — Dialog control
 *   canRegenerate  (bool, default true)— show the regenerate button (admin/auditor)
 *
 * Auto-loads cached report on open. If none exists, shows "Generate" CTA.
 */
export default function CumulativeLearningReport({
  userId,
  userName,
  open,
  onOpenChange,
  canRegenerate = true,
}) {
  const [loading, setLoading] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [report, setReport] = useState(null);
  const [stats, setStats] = useState(null);
  const [generatedAt, setGeneratedAt] = useState(null);
  const [stale, setStale] = useState(false);
  const [exists, setExists] = useState(null); // null = unknown, true/false after first load

  const loadCached = useCallback(async () => {
    if (!userId) return;
    setLoading(true);
    try {
      const { data } = await api.get(`/users/${userId}/cumulative-report`);
      if (data?.exists) {
        setReport(data.report);
        setStats(data.stats);
        setGeneratedAt(data.generated_at);
        setStale(!!data.stale);
        setExists(true);
      } else {
        setExists(false);
      }
    } catch (err) {
      console.error('Failed to load cumulative report:', err);
      toast.error(err.response?.data?.detail || 'Could not load coaching report');
      setExists(false);
    } finally {
      setLoading(false);
    }
  }, [userId]);

  const generateFresh = useCallback(async () => {
    if (!userId || generating) return;
    setGenerating(true);
    try {
      const { data } = await api.post(`/users/${userId}/cumulative-report`, DEFAULT_FILTERS);
      setReport(data.report);
      setStats(data.stats);
      setGeneratedAt(data.generated_at);
      setStale(false);
      setExists(true);
      toast.success('Coaching report generated.');
    } catch (err) {
      console.error('Failed to generate cumulative report:', err);
      toast.error(err.response?.data?.detail || 'Generation failed — try again.');
    } finally {
      setGenerating(false);
    }
  }, [userId, generating]);

  useEffect(() => {
    if (open && userId) {
      setReport(null); setStats(null); setExists(null);
      loadCached();
    }
  }, [open, userId, loadCached]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        data-testid="cumulative-report-dialog"
        className="max-w-5xl max-h-[92vh] overflow-y-auto p-0"
      >
        <DialogHeader className="px-6 pt-6 pb-4 border-b border-slate-200 dark:border-slate-800 bg-gradient-to-br from-violet-50/60 to-fuchsia-50/30 dark:from-violet-950/20 dark:to-fuchsia-950/10">
          <div className="flex items-start justify-between gap-3">
            <div className="flex items-center gap-3">
              <div className="h-10 w-10 rounded-xl bg-gradient-to-br from-violet-500 to-fuchsia-500 flex items-center justify-center shadow-md">
                <Sparkles className="h-5 w-5 text-white" />
              </div>
              <div>
                <DialogTitle className="text-xl">
                  Cumulative Coaching Report{userName ? ` — ${userName}` : ''}
                </DialogTitle>
                <DialogDescription className="text-xs mt-0.5">
                  AI-synthesized insights across every completed call. Updated every 24 hours.
                </DialogDescription>
              </div>
            </div>
            {canRegenerate && exists && (
              <Button
                variant="outline"
                size="sm"
                data-testid="cumulative-report-regenerate-btn"
                onClick={generateFresh}
                disabled={generating}
                className="gap-2"
              >
                <RefreshCw className={`h-3.5 w-3.5 ${generating ? 'animate-spin' : ''}`} />
                {generating ? 'Regenerating…' : 'Regenerate'}
              </Button>
            )}
          </div>
        </DialogHeader>

        <div className="p-6 space-y-5">
          {loading && <ReportSkeleton />}

          {!loading && exists === false && (
            <EmptyState onGenerate={generateFresh} generating={generating} canRegenerate={canRegenerate} />
          )}

          {!loading && exists && report && (
            <ReportContent
              report={report}
              stats={stats}
              generatedAt={generatedAt}
              stale={stale}
            />
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}


function ReportSkeleton() {
  return (
    <div className="space-y-4">
      <Skeleton className="h-24 w-full" />
      <div className="grid grid-cols-4 gap-3">
        {[...Array(4)].map((_, i) => <Skeleton key={i} className="h-20" />)}
      </div>
      <Skeleton className="h-32 w-full" />
      <Skeleton className="h-32 w-full" />
    </div>
  );
}


function EmptyState({ onGenerate, generating, canRegenerate }) {
  return (
    <Card
      data-testid="cumulative-report-empty-state"
      className="border-2 border-dashed border-violet-200 dark:border-violet-900 bg-gradient-to-br from-violet-50/40 to-fuchsia-50/30 dark:from-violet-950/10 dark:to-fuchsia-950/10"
    >
      <CardContent className="p-8 text-center space-y-4">
        <div className="h-14 w-14 mx-auto rounded-2xl bg-gradient-to-br from-violet-500 to-fuchsia-500 flex items-center justify-center shadow-md">
          <Sparkles className="h-7 w-7 text-white" />
        </div>
        <div className="space-y-1.5 max-w-md mx-auto">
          <h3 className="font-semibold text-lg text-slate-900 dark:text-slate-100">
            No coaching report yet
          </h3>
          <p className="text-sm text-slate-600 dark:text-slate-400 leading-relaxed">
            Generate an AI-synthesized cumulative report from all completed calls. Highlights recurring strengths,
            common misses with rephrased scripts, a 4-week practice plan, and a manager-brief.
          </p>
          <p className="text-xs text-slate-500 italic">Takes ~20 seconds. Cached for 24 hours.</p>
        </div>
        {canRegenerate ? (
          <Button
            data-testid="cumulative-report-generate-btn"
            onClick={onGenerate}
            disabled={generating}
            className="gap-2 bg-gradient-to-r from-violet-600 to-fuchsia-600 hover:from-violet-700 hover:to-fuchsia-700 text-white"
          >
            <Sparkles className="h-4 w-4" />
            {generating ? 'Generating coaching report…' : 'Generate coaching report'}
          </Button>
        ) : (
          <p className="text-xs text-slate-500">
            Your coaching report hasn't been generated yet. Please ask your admin or auditor to generate one.
          </p>
        )}
      </CardContent>
    </Card>
  );
}


function ReportContent({ report, stats, generatedAt, stale }) {
  const tier = report?.headline_metrics?.performance_tier;
  const trend = report?.headline_metrics?.trend;
  const tierStyle = TIER_STYLES[tier] || TIER_STYLES['Solid'];
  const TrendIcon = TREND_ICON[trend] || Minus;
  const generated = generatedAt ? new Date(generatedAt) : null;
  const ageHours = generated ? Math.round((Date.now() - generated.getTime()) / 36e5) : null;

  return (
    <div className="space-y-5">
      {/* Staleness banner */}
      {stale && (
        <div data-testid="cumulative-report-stale-banner" className="px-3 py-2 rounded-lg bg-amber-50 dark:bg-amber-950/30 border border-amber-200 dark:border-amber-900 flex items-center gap-2 text-xs text-amber-800 dark:text-amber-200">
          <Clock className="h-3.5 w-3.5" />
          This report is older than 24 hours. Hit <strong>Regenerate</strong> to refresh it.
        </div>
      )}

      {/* Headline: tier + trend + executive summary */}
      <Card data-testid="cumulative-report-headline" className="border-2 border-violet-200 dark:border-violet-900 overflow-hidden">
        <div className={`bg-gradient-to-r ${tierStyle.bg} px-5 py-4`}>
          <div className="flex items-center justify-between gap-4 flex-wrap">
            <div className="flex items-center gap-3 text-white">
              <span className="text-3xl">{tierStyle.emoji}</span>
              <div>
                <p className="text-[10px] uppercase tracking-widest opacity-80">Performance tier</p>
                <p className="text-xl font-bold">{tier || 'Solid'}</p>
              </div>
            </div>
            <div className="flex items-center gap-2 text-white bg-black/20 px-3 py-1.5 rounded-full">
              <TrendIcon className="h-4 w-4" />
              <span className="text-sm font-medium">{trend || 'Stable'}</span>
            </div>
          </div>
        </div>
        <CardContent className="p-5 space-y-3">
          {report?.executive_summary && (
            <p className="text-sm text-slate-700 dark:text-slate-300 leading-relaxed">
              {report.executive_summary}
            </p>
          )}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3 pt-2">
            {report?.headline_metrics?.biggest_strength && (
              <div className="p-3 rounded-lg bg-emerald-50/60 dark:bg-emerald-950/20 border border-emerald-100 dark:border-emerald-900">
                <p className="text-[10px] uppercase tracking-wider text-emerald-700 dark:text-emerald-300 font-semibold mb-1 flex items-center gap-1">
                  <Trophy className="h-3 w-3" /> Biggest strength
                </p>
                <p className="text-sm text-slate-800 dark:text-slate-200">{report.headline_metrics.biggest_strength}</p>
              </div>
            )}
            {report?.headline_metrics?.biggest_gap && (
              <div className="p-3 rounded-lg bg-amber-50/60 dark:bg-amber-950/20 border border-amber-100 dark:border-amber-900">
                <p className="text-[10px] uppercase tracking-wider text-amber-700 dark:text-amber-300 font-semibold mb-1 flex items-center gap-1">
                  <AlertCircle className="h-3 w-3" /> Biggest gap
                </p>
                <p className="text-sm text-slate-800 dark:text-slate-200">{report.headline_metrics.biggest_gap}</p>
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Quick stats grid */}
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="cumulative-report-stats">
          <StatCard label="Calls analysed" value={stats.total_calls} />
          <StatCard label="Avg score" value={stats.score?.avg?.toFixed?.(1) || stats.score?.avg} />
          <StatCard label="Hours of audio" value={stats.duration?.total_hours} suffix="h" />
          <StatCard
            label="Compliance issues"
            value={stats.compliance?.compliance_alert_calls || 0}
            suffix={` / ${stats.total_calls}`}
            tone={(stats.compliance?.compliance_alert_pct || 0) > 30 ? 'warning' : 'neutral'}
          />
        </div>
      )}

      {/* What you're doing well */}
      {(report?.what_youre_doing_well?.length ?? 0) > 0 && (
        <Section icon={Trophy} title="What you're doing well" accent="emerald" testid="cumulative-report-wins">
          {report.what_youre_doing_well.map((item, i) => (
            <Card key={i} className="border-l-4 border-l-emerald-500">
              <CardContent className="p-4 space-y-1.5">
                <p className="font-medium text-sm text-slate-900 dark:text-slate-100">{item.pattern}</p>
                {item.evidence && (
                  <p className="text-xs text-emerald-700 dark:text-emerald-400">
                    <span className="font-semibold">Evidence: </span>{item.evidence}
                  </p>
                )}
                {item.example_quote && (
                  <p className="text-xs italic text-slate-600 dark:text-slate-400">"{item.example_quote}"</p>
                )}
              </CardContent>
            </Card>
          ))}
        </Section>
      )}

      {/* Where you're losing points */}
      {(report?.where_youre_losing_points?.length ?? 0) > 0 && (
        <Section icon={AlertCircle} title="Where you're losing points" accent="amber" testid="cumulative-report-misses">
          {report.where_youre_losing_points.map((item, i) => (
            <Card key={i} className="border-l-4 border-l-amber-500">
              <CardContent className="p-4 space-y-2">
                <div className="flex items-start justify-between gap-3 flex-wrap">
                  <p className="font-medium text-sm text-slate-900 dark:text-slate-100">{item.pattern}</p>
                  {item.frequency && (
                    <Badge variant="outline" className="text-[10px] bg-amber-50 border-amber-200 text-amber-700">
                      {item.frequency}
                    </Badge>
                  )}
                </div>
                {item.example_quote && (
                  <div className="space-y-1">
                    <p className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">Example</p>
                    <p className="text-xs italic text-slate-700 dark:text-slate-300 p-2 bg-red-50/60 dark:bg-red-950/20 rounded border border-red-100 dark:border-red-900">"{item.example_quote}"</p>
                  </div>
                )}
                {item.rephrased_script && (
                  <div className="space-y-1">
                    <p className="text-[10px] uppercase tracking-wider text-emerald-700 dark:text-emerald-400 font-semibold">Try this instead</p>
                    <p className="text-xs font-medium text-slate-800 dark:text-slate-200 p-2 bg-emerald-50/60 dark:bg-emerald-950/20 rounded border border-emerald-100 dark:border-emerald-900">"{item.rephrased_script}"</p>
                  </div>
                )}
                {item.impact && (
                  <p className="text-xs text-slate-600 dark:text-slate-400">
                    <span className="font-semibold text-amber-700 dark:text-amber-400">Impact: </span>{item.impact}
                  </p>
                )}
              </CardContent>
            </Card>
          ))}
        </Section>
      )}

      {/* Compliance rollup */}
      {report?.compliance_audit_rollup && (
        <Section icon={ShieldCheck} title="Compliance audit rollup" accent="indigo" testid="cumulative-report-compliance">
          <Card>
            <CardContent className="p-4 space-y-3">
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                <PctCell label="Branding" value={report.compliance_audit_rollup.branding_pass_rate} />
                <PctCell label="Disclaimer" value={report.compliance_audit_rollup.disclaimer_pass_rate} />
                <PctCell label="Probing" value={report.compliance_audit_rollup.probing_pass_rate} />
                <PctCell label="RTR" value={report.compliance_audit_rollup.rtr_pass_rate} />
              </div>
              {report.compliance_audit_rollup.fatal_violations_count && (
                <div className="text-xs text-red-700 dark:text-red-400 font-medium">
                  Fatal violations: {report.compliance_audit_rollup.fatal_violations_count}
                </div>
              )}
              {report.compliance_audit_rollup.narrative && (
                <p className="text-sm text-slate-700 dark:text-slate-300 pt-2 border-t border-slate-100 dark:border-slate-800">
                  {report.compliance_audit_rollup.narrative}
                </p>
              )}
            </CardContent>
          </Card>
        </Section>
      )}

      {/* 30-day plan */}
      {(report?.personalised_30_day_plan?.length ?? 0) > 0 && (
        <Section icon={Calendar} title="Your 4-week practice plan" accent="violet" testid="cumulative-report-plan">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {report.personalised_30_day_plan.map((wk, i) => (
              <Card key={i} className="border-l-4 border-l-violet-500" data-testid={`cumulative-report-week-${wk.week ?? i + 1}`}>
                <CardContent className="p-4 space-y-1.5">
                  <Badge className="bg-violet-100 text-violet-700 border-violet-200 text-[10px]">Week {wk.week ?? i + 1}</Badge>
                  <p className="font-medium text-sm text-slate-900 dark:text-slate-100">{wk.focus_area}</p>
                  {wk.daily_drill && (
                    <p className="text-xs text-slate-700 dark:text-slate-300">
                      <span className="font-semibold text-violet-700 dark:text-violet-400">Daily drill: </span>{wk.daily_drill}
                    </p>
                  )}
                  {wk.success_metric && (
                    <p className="text-xs text-emerald-700 dark:text-emerald-400">
                      <Target className="h-3 w-3 inline-block mr-1 -mt-0.5" />
                      <span className="font-semibold">Success: </span>{wk.success_metric}
                    </p>
                  )}
                </CardContent>
              </Card>
            ))}
          </div>
        </Section>
      )}

      {/* Recommended modules */}
      {(report?.recommended_modules?.length ?? 0) > 0 && (
        <Section icon={BookOpen} title="Recommended modules to retake" accent="blue" testid="cumulative-report-modules">
          <div className="space-y-2">
            {report.recommended_modules.map((m, i) => (
              <div key={i} className="flex items-start gap-3 p-3 rounded-lg border border-slate-200 dark:border-slate-800 hover:bg-slate-50/60 dark:hover:bg-slate-900/30">
                <ChevronRight className="h-4 w-4 text-blue-500 mt-0.5 shrink-0" />
                <div className="min-w-0 flex-1">
                  <p className="font-medium text-sm text-slate-900 dark:text-slate-100">{m.module}</p>
                  {m.why && <p className="text-xs text-slate-600 dark:text-slate-400 mt-0.5">{m.why}</p>}
                </div>
              </div>
            ))}
          </div>
        </Section>
      )}

      {/* Manager brief */}
      {report?.manager_brief && (
        <Card data-testid="cumulative-report-manager-brief" className="border-2 border-indigo-200 dark:border-indigo-900">
          <CardContent className="p-5">
            <div className="flex items-center gap-2 mb-2">
              <Briefcase className="h-4 w-4 text-indigo-600" />
              <h4 className="font-semibold text-slate-900 dark:text-slate-100 text-sm">Manager brief (10-second read)</h4>
            </div>
            <p className="text-sm text-slate-700 dark:text-slate-300 leading-relaxed">
              {report.manager_brief}
            </p>
          </CardContent>
        </Card>
      )}

      {/* Reflection prompt */}
      {report?.self_reflection_prompt && (
        <Card data-testid="cumulative-report-reflection" className="border-2 border-fuchsia-200 dark:border-fuchsia-900 bg-gradient-to-br from-fuchsia-50/60 to-pink-50/30 dark:from-fuchsia-950/20 dark:to-pink-950/10">
          <CardContent className="p-5">
            <div className="flex items-center gap-2 mb-2">
              <Lightbulb className="h-4 w-4 text-fuchsia-600" />
              <p className="text-[10px] uppercase tracking-wider text-fuchsia-700 dark:text-fuchsia-300 font-semibold">
                Reflection
              </p>
            </div>
            <p className="text-base font-medium italic text-slate-900 dark:text-slate-100 leading-relaxed">
              "{report.self_reflection_prompt}"
            </p>
            <p className="text-xs text-slate-500 mt-2">Journal on this before your next call.</p>
          </CardContent>
        </Card>
      )}

      {generated && (
        <p className="text-[10px] text-slate-400 text-center pt-2">
          Generated {generated.toLocaleString()} • {ageHours != null && ageHours < 1 ? 'just now' : `${ageHours}h ago`}
        </p>
      )}
    </div>
  );
}


function Section({ icon: Icon, title, accent, testid, children }) {
  const color = `text-${accent}-600`;
  return (
    <section data-testid={testid} className="space-y-3">
      <div className="flex items-center gap-2">
        <Icon className={`h-4 w-4 ${color}`} />
        <h4 className="font-semibold text-slate-900 dark:text-slate-100">{title}</h4>
      </div>
      <div className="space-y-2.5">{children}</div>
    </section>
  );
}

function StatCard({ label, value, suffix = '', tone = 'neutral' }) {
  const toneClasses = {
    neutral: 'bg-slate-50 dark:bg-slate-900/40 border-slate-200 dark:border-slate-800',
    warning: 'bg-amber-50 dark:bg-amber-950/30 border-amber-200 dark:border-amber-900',
  }[tone];
  return (
    <Card className={`${toneClasses} border`}>
      <CardContent className="p-3">
        <p className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold mb-1">{label}</p>
        <p className="text-2xl font-bold text-slate-900 dark:text-slate-100">
          {value ?? '—'}{suffix}
        </p>
      </CardContent>
    </Card>
  );
}

function PctCell({ label, value }) {
  const num = parseInt(String(value).replace('%', ''), 10);
  const tone = isNaN(num)
    ? 'text-slate-500'
    : num >= 90
      ? 'text-emerald-700 dark:text-emerald-400'
      : num >= 70
        ? 'text-amber-700 dark:text-amber-400'
        : 'text-red-700 dark:text-red-400';
  return (
    <div className="p-3 rounded-lg border border-slate-200 dark:border-slate-800">
      <p className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold mb-0.5">{label}</p>
      <p className={`text-xl font-bold ${tone}`}>{value || '—'}</p>
    </div>
  );
}

export { CumulativeLearningReport };
